/**
 * Participant invitation service
 * Handles invitation lifecycle, expiration tracking, and resend logic
 */

import type { Participant } from '@/types/evaluation.types';
import { InvitationStatus } from '@/types/evaluation.types';

/**
 * Check if a participant's invitation has expired
 * @param participant - The participant to check
 * @returns true if invitation is expired, false otherwise
 */
export function isInvitationExpired(participant: Participant): boolean {
  // Accepted invitations never expire
  if (participant.invitationStatus === InvitationStatus.Accepted) {
    return false;
  }

  // If no expiration date set, invitation hasn't expired
  if (!participant.invitationExpirationDate) {
    return false;
  }

  // Check if expiration date is in the past
  const now = new Date();
  const expirationDate = new Date(participant.invitationExpirationDate);
  return expirationDate < now;
}

/**
 * Calculate a new invitation expiration date
 * @param daysUntilExpiration - Number of days until invitation expires
 * @returns New expiration date
 */
export function calculateExpirationDate(daysUntilExpiration: number): Date {
  const expirationDate = new Date();
  expirationDate.setDate(expirationDate.getDate() + daysUntilExpiration);
  return expirationDate;
}

/**
 * Resend invitation to a participant (updates invited date and expiration)
 * @param participant - The participant to resend invitation to
 * @param daysUntilExpiration - Number of days until invitation expires
 * @returns Updated participant with new invitation dates
 */
export function resendInvitation(
  participant: Participant,
  daysUntilExpiration: number
): Participant {
  const invitedDate = new Date();
  const expirationDate = calculateExpirationDate(daysUntilExpiration);

  return {
    ...participant,
    invitedDate,
    invitationExpirationDate: expirationDate,
    invitationStatus: InvitationStatus.Pending, // Reset to pending
  };
}

/**
 * Check if resend invitation button should be enabled
 * @param participant - The participant to check
 * @returns true if resend should be enabled, false otherwise
 */
export function canResendInvitation(participant: Participant): boolean {
  // Cannot resend if participant has accepted
  if (participant.invitationStatus === InvitationStatus.Accepted) {
    return false;
  }

  // Can resend if pending or declined
  return true;
}

/**
 * Format days remaining until expiration
 * @param participant - The participant to check
 * @returns Number of days remaining (negative if expired), or null if no expiration
 */
export function getDaysUntilExpiration(participant: Participant): number | null {
  // No expiration for accepted invitations
  if (participant.invitationStatus === InvitationStatus.Accepted) {
    return null;
  }

  // No expiration date set
  if (!participant.invitationExpirationDate) {
    return null;
  }

  const now = new Date();
  const expirationDate = new Date(participant.invitationExpirationDate);
  const diffMs = expirationDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  return diffDays;
}
